import type { ChartStaticRow, StaticSeries } from '@trinity/chat/types/dashboard-config';
import type { ChartWidgetData } from '@trinity/chat/types/dashboard-data';
import { CHART_EXAMPLES } from '@trinity/chat/types/dashboard-examples';
import { Colors } from '@trinity/chat-components/components/ColorSelect/Colors';
import { DEFAULT_COLORS } from '@trinity/chat-components/lib/chart-colors';

/**
 * Everything the dashboard UI knows about one chart kind (the `chart` widget's sub-types), in one
 * place. `CHART_KINDS` is a record over every kind, so adding a kind to `ChartWidgetDataSchema` is a
 * type error here until the kind says how it is labelled, edited, coloured, sampled and listed in legends.
 * The React rendering for each kind is in `ChartWidgetRouter.tsx`, which is already exhaustive.
 */

export type ChartType = ChartWidgetData['type'];
export type ChartDataOf<K extends ChartType> = Extract<ChartWidgetData, { type: K }>;

export type LegendItem = { key: string; color: string; label?: string };

/**
 * Which static-data form a kind uses in the builder.
 * `none`: the builder has no static-data form for it.
 */
export type ChartDataEntry = 'bar' | 'pie' | 'series' | 'kpi' | 'progress' | 'table' | 'none';

/** What the static-data form collects, which decides how a kind's data is read back and validated. */
export type ChartDataShape = 'rows' | 'series' | 'kpi' | 'table';

/** The builder form's values for a chart. */
export interface ChartFormValues {
  title: string;
  xAxisLabel: string;
  yAxisLabel: string;
  rows: ChartStaticRow[];
  series: StaticSeries[];
}

// Method syntax (bivariant) on purpose: it lets `kindFor(type)` be called with a union of kinds.
export interface ChartKind<K extends ChartType> {
  /** Label in the chart-type dropdown. */
  readonly label: string;
  readonly showXAxis: boolean;
  readonly showYAxis: boolean;
  readonly dataEntry: ChartDataEntry;
  /** Series (or category) names the colour overrides apply to; empty when the kind has none. */
  seriesKeys(data: ChartDataOf<K>): string[];
  /** Colours embedded in the data (set by agents); undefined when the kind ignores them. */
  dataColors(data: ChartDataOf<K>): string[] | undefined;
  /** The data with the resolved colours applied. */
  withColors(data: ChartDataOf<K>, colors: string[]): ChartDataOf<K>;
  legend(data: ChartDataOf<K>, colors: string[]): LegendItem[];
  /** Sample data shown in the Data tab. A line chart's sample is a one-series multiple-line chart. */
  example(): ChartWidgetData;
  /** Data built from the builder's form values. A line chart builds a one-series multiple-line chart. */
  buildData(values: ChartFormValues): ChartWidgetData;
}

const EXAMPLE_OPTIONS = { headerColor: DEFAULT_COLORS[0] };

/** A kind's sample data, from the examples kept next to the schemas. */
const exampleOf =
  (kind: ChartType) =>
  (): ChartWidgetData =>
    CHART_EXAMPLES[kind](EXAMPLE_OPTIONS);

const NO_KEYS = (): string[] => [];
const NO_LEGEND = (): LegendItem[] => [];
const NO_DATA_COLORS = (): undefined => undefined;

const points = (rows: ChartStaticRow[]): { x: string; y: number }[] =>
  rows.map((r) => ({ x: String(r.category), y: Number(r.value) || 0 }));

const withColorsField =
  <K extends ChartType>() =>
  (data: ChartDataOf<K>, colors: string[]): ChartDataOf<K> => ({ ...data, colors });

const seriesColors = (keys: string[], colors: string[]): LegendItem[] =>
  keys.map((key, i) => ({ key, color: colors[i % colors.length] }));

/** Keys of a multi-bar chart: the distinct x values across its series, in order of appearance. */
function multiBarKeys(data: ChartDataOf<'multiple-clustered-bar' | 'multiple-stacked-bar'>): string[] {
  const seen = new Set<string>();
  for (const row of data.chart.data) {
    for (const point of row.data) seen.add(point.x);
  }
  return Array.from(seen);
}


function multiBarData(type: 'multiple-clustered-bar' | 'multiple-stacked-bar', v: ChartFormValues): ChartWidgetData {
  return {
    type,
    chart: {
      title: v.title,
      xAxisTitle: v.xAxisLabel,
      yAxisTitle: v.yAxisLabel,
      indexBy: 'x',
      data: v.series.map((s) => ({
        index: s.name || 'Series',
        data: s.rows.map((r) => ({ x: String(r.category), y: Number(r.value) || 0 })),
      })),
    },
  };
}


function simpleBarData(type: 'bar' | 'stacked-bar', v: ChartFormValues): ChartWidgetData {
  return {
    type,
    chart: { title: v.title, xAxisTitle: v.xAxisLabel, yAxisTitle: v.yAxisLabel, indexBy: 'x', data: points(v.rows) },
  };
}

const simpleBarKeys = (data: ChartDataOf<'bar' | 'stacked-bar'>): string[] => data.chart.data.map((d) => d.x);


function kpiData(
  type: 'kpi-widget' | 'progress-bar-widget' | 'semi-circle-progress-widget',
  v: ChartFormValues
): ChartWidgetData {
  return {
    type,
    title: v.title,
    backgroundColor: '#ffffff',
    values: v.rows.map((r) => String(r.value)),
    labels: v.rows.map((r) => String(r.category)),
  };
}

/** Line charts and multiple-line charts share data: every line chart is built as multiple-line data. */
const lineSeriesLegend = (data: ChartDataOf<'multiple-line'>, colors: string[]): LegendItem[] =>
  data.chart.data.map((series, i) => ({
    // series.id is the stable unique key; the label is the display text when there is one.
    key: series.id,
    label: series.label ?? series.id,
    color: colors[i % colors.length],
  }));

/** The chart-kind record: a new chart type is a type error here until it is added. */
export const CHART_KINDS: { [K in ChartType]: ChartKind<K> } = {
  bar: {
    label: 'Bar Chart',
    showXAxis: true,
    showYAxis: true,
    dataEntry: 'bar',
    seriesKeys: simpleBarKeys,
    dataColors: (data) => data.colors,
    withColors: withColorsField<'bar'>(),
    legend: (data, colors) => seriesColors(simpleBarKeys(data), colors),
    example: exampleOf('bar'),
    buildData: (v) => simpleBarData('bar', v),
  },
  'stacked-bar': {
    label: 'Stacked Bar',
    showXAxis: true,
    showYAxis: true,
    dataEntry: 'bar',
    seriesKeys: simpleBarKeys,
    dataColors: (data) => data.colors,
    withColors: withColorsField<'stacked-bar'>(),
    legend: (data, colors) => seriesColors(simpleBarKeys(data), colors),
    example: exampleOf('stacked-bar'),
    buildData: (v) => simpleBarData('stacked-bar', v),
  },
  pie: {
    label: 'Pie Chart',
    showXAxis: false,
    showYAxis: false,
    dataEntry: 'pie',
    seriesKeys: (data) => data.chart.data.map((d) => d.label),
    dataColors: (data) => data.colors,
    withColors: withColorsField<'pie'>(),
    legend: (data, colors) =>
      seriesColors(
        data.chart.data.map((d) => d.label),
        colors
      ),
    example: exampleOf('pie'),
    buildData: (v) => ({
      type: 'pie',
      chart: {
        title: v.title,
        xAxisTitle: v.xAxisLabel,
        yAxisTitle: v.yAxisLabel,
        data: v.rows.map((r) => ({ id: String(r.category), label: String(r.category), value: Number(r.value) || 0 })),
      },
    }),
  },
  line: {
    label: 'Line Chart',
    showXAxis: false,
    showYAxis: true,
    dataEntry: 'none',
    seriesKeys: NO_KEYS,
    dataColors: NO_DATA_COLORS,
    withColors: withColorsField<'line'>(),
    legend: NO_LEGEND, // a single series needs no legend
    example: exampleOf('line'),
    buildData: (v) => ({
      type: 'multiple-line',
      chart: {
        title: v.title,
        xAxisTitle: v.xAxisLabel,
        yAxisTitle: v.yAxisLabel,
        data: [{ id: 'series', data: points(v.rows) }],
      },
    }),
  },
  'multiple-line': {
    label: 'Multiple Line',
    showXAxis: true,
    showYAxis: true,
    dataEntry: 'series',
    seriesKeys: NO_KEYS,
    dataColors: NO_DATA_COLORS,
    withColors: withColorsField<'multiple-line'>(),
    legend: lineSeriesLegend,
    example: exampleOf('multiple-line'),
    buildData: (v) => ({
      type: 'multiple-line',
      chart: {
        title: v.title,
        xAxisTitle: v.xAxisLabel,
        yAxisTitle: v.yAxisLabel,
        data: v.series.map((s) => ({
          id: s.name || 'Series',
          data: s.rows.map((r) => ({ x: String(r.category), y: Number(r.value) || 0 })),
        })),
      },
    }),
  },
  'multiple-clustered-bar': {
    label: 'Clustered Bar',
    showXAxis: true,
    showYAxis: true,
    dataEntry: 'series',
    seriesKeys: multiBarKeys,
    dataColors: (data) => data.colors,
    withColors: withColorsField<'multiple-clustered-bar'>(),
    legend: (data, colors) => seriesColors(multiBarKeys(data), colors),
    example: exampleOf('multiple-clustered-bar'),
    buildData: (v) => multiBarData('multiple-clustered-bar', v),
  },
  'multiple-stacked-bar': {
    label: 'Multiple Stacked Bar',
    showXAxis: true,
    showYAxis: true,
    dataEntry: 'series',
    seriesKeys: multiBarKeys,
    dataColors: (data) => data.colors,
    withColors: withColorsField<'multiple-stacked-bar'>(),
    legend: (data, colors) => seriesColors(multiBarKeys(data), colors),
    example: exampleOf('multiple-stacked-bar'),
    buildData: (v) => multiBarData('multiple-stacked-bar', v),
  },
  'data-table': {
    label: 'Data Table',
    showXAxis: false,
    showYAxis: false,
    dataEntry: 'table',
    seriesKeys: NO_KEYS,
    dataColors: NO_DATA_COLORS,
    withColors: (data, colors) => ({ ...data, headerColor: data.headerColor || colors[0] || DEFAULT_COLORS[0] }),
    legend: NO_LEGEND,
    example: exampleOf('data-table'),
    // The builder normally builds tables from its column/row editor; this is the fallback from category/value rows.
    buildData: (v) => ({
      type: 'data-table',
      title: v.title,
      headerColor: Colors.defaultColor,
      data: {
        columnNames: [['Category'], ['Value']],
        rowNames: v.rows.map((_, i) => [String(i + 1)]),
        records: v.rows.map((r) => [String(r.category), String(r.value)]),
      },
    }),
  },
  'kpi-widget': {
    label: 'KPI Widget',
    showXAxis: false,
    showYAxis: false,
    dataEntry: 'kpi',
    seriesKeys: NO_KEYS,
    dataColors: NO_DATA_COLORS,
    withColors: (data) => data,
    legend: NO_LEGEND,
    example: exampleOf('kpi-widget'),
    buildData: (v) => kpiData('kpi-widget', v),
  },
  'progress-bar-widget': {
    label: 'Progress Bar',
    showXAxis: false,
    showYAxis: false,
    dataEntry: 'progress',
    seriesKeys: NO_KEYS,
    dataColors: NO_DATA_COLORS,
    withColors: withColorsField<'progress-bar-widget'>(),
    legend: NO_LEGEND,
    example: exampleOf('progress-bar-widget'),
    buildData: (v) => kpiData('progress-bar-widget', v),
  },
  'semi-circle-progress-widget': {
    label: 'Semi Circle Progress',
    showXAxis: false,
    showYAxis: false,
    dataEntry: 'progress',
    seriesKeys: NO_KEYS,
    dataColors: NO_DATA_COLORS,
    withColors: withColorsField<'semi-circle-progress-widget'>(),
    legend: NO_LEGEND,
    example: exampleOf('semi-circle-progress-widget'),
    buildData: (v) => kpiData('semi-circle-progress-widget', v),
  },
};

/** The kind for a chart type. Pass a union to get a kind that accepts any of them. */
export function kindFor<K extends ChartType>(type: K): ChartKind<K> {
  return CHART_KINDS[type];
}

/** What the builder's static-data form collects for a data entry. */
export function dataShapeOf(entry: ChartDataEntry): ChartDataShape {
  switch (entry) {
    case 'series':
      return 'series';
    case 'kpi':
    case 'progress':
      return 'kpi';
    case 'table':
      return 'table';
    case 'bar':
    case 'pie':
    case 'none':
      return 'rows';
    default: {
      const _exhaustive: never = entry;
      return 'rows';
    }
  }
}
